import { Link, Text } from '@customerio/pluma-components/react';
import type { Delivery } from '@/services/db/delivery/entity';
import { generatePath } from '@/services/router/generate-path';
import { requireWorkspaceId } from '@/services/db/shared/workspace';
import { isEmptyString } from '@/utils/string';

type RecipientCellProps = { delivery: Delivery };

/**
 * "Recipient / People" cell. Ported from Ember `cells/recipient`: links to the
 * person profile when the delivery has a `customer_id`, otherwise shows the
 * recipient with a "(deleted person)" / "(anonymous)" qualifier. Webhook rows
 * show the customer id as their label. Delivery-mode badge deferred.
 */
export function RecipientCell({ delivery }: RecipientCellProps) {
  const recipient = isEmptyString(delivery.recipient) ? 'No recipient' : delivery.recipient;

  if (delivery.customer_deleted === true) {
    return <Text className="fs-mask" color="subtle" truncate>{`${recipient} (deleted person)`}</Text>;
  }

  if (!isEmptyString(delivery.customer_id)) {
    const workspaceId = requireWorkspaceId();
    const href = generatePath('/workspaces/:workspaceId/journeys/people/:personId', {
      workspaceId,
      personId: delivery.customer_id,
    });
    const label = delivery.action_type === 'webhook_action' ? delivery.customer_id : recipient;
    // No `Tooltip` on the recipient: its `content` is a plain string that renders in an
    // unmaskable portal, and the recipient (often an email) is PII we keep out of FullStory
    // (the link is fs-masked). The person profile it links to shows the full value.
    return (
      <Link className="fs-mask" href={href} variant="secondary" truncate>
        {label}
      </Link>
    );
  }

  return <Text className="fs-mask" truncate>{`${recipient} (anonymous)`}</Text>;
}
